<template>
  <div class="host-contact">
    <form v-if="state !== 'success'" @submit.prevent="send" :aria-busy="state === 'pending'">
      <h2>{{ firstName ? 'Connect with ' + firstName : 'Connect with your community host' }}</h2>
      <fieldset :disabled="state === 'pending'">
        <template v-if="!username">
          <label for="host-contact-name">Your name</label>
          <input id="host-contact-name" v-model="form.firstName" autocomplete="name" maxlength="256" />
        </template>
        <label for="host-contact-message">Your message (optional)</label>
        <textarea id="host-contact-message" v-model="form.message" rows="4" maxlength="5000"
          placeholder="Ask a question or tell your host a little about yourself." />
        <p v-if="errors.message" class="field-error" role="alert">{{ errorText(errors.message) }}</p>
        <fieldset class="methods">
          <legend>How would you like your host to reply?</legend>
          <p class="hint">Choose one or more.</p>
          <label v-for="method in methods" :key="method.key" class="contact-method">
            <input type="checkbox" v-model="form[method.key]" /> {{ method.label }}
          </label>
          <p v-if="errors.methods" class="field-error" role="alert">{{ errorText(errors.methods) }}</p>
        </fieldset>
        <template v-for="method in methods">
          <div v-if="form[method.key] && !(username && method.key === 'methodEmail')" :key="method.key">
            <label :for="'host-contact-' + method.field">{{ method.detail }}</label>
            <input :id="'host-contact-' + method.field" v-model="form[method.field]"
              :type="method.type" :autocomplete="method.autocomplete" required :maxlength="method.maxlength"
              :aria-invalid="Boolean(errors[method.field])" :aria-describedby="errors[method.field] ? 'host-error-' + method.field : null" />
            <p v-if="errors[method.field]" :id="'host-error-' + method.field" class="field-error" role="alert">{{ errorText(errors[method.field]) }}</p>
          </div>
        </template>
        <p v-if="username && form.methodEmail" class="hint">Your host will use your account email address.</p>
        <p v-if="error" role="alert" class="field-error">{{ error }}</p>
        <button type="submit" class="send-button" :disabled="!hasMethod || state === 'pending'">
          {{ state === 'pending' ? 'Sending…' : firstName ? 'Send to ' + firstName : 'Send to your host' }}
        </button>
        <button type="button" class="back-link" @click="close">Back</button>
      </fieldset>
    </form>
    <div v-else class="success" role="status" tabindex="-1" ref="success">
      <h2>{{ firstName ? 'Your message has been sent to ' + firstName + '.' : 'Your message has been sent to your community host.' }}</h2>
      <p>Your host will reply through {{ selectedMethods }}.</p>
      <button type="button" class="back-link" @click="close">Back</button>
    </div>
  </div>
</template>
<script>
import { usableHostFirstName } from './hostName'

export default {
  name: 'Connect',
  props: { connectResult: Object, username: String, host: Object },
  data () {
    return {
      form: {firstName: '', lastName: '', message: '', email: '', phone: '', instagram: '', methodEmail: false, methodText: false, methodInstagram: false},
      methods: [
        {key: 'methodEmail', label: 'Email', field: 'email', detail: 'Your email address', type: 'email', autocomplete: 'email', maxlength: 256},
        {key: 'methodText', label: 'Text', field: 'phone', detail: 'Your phone number', type: 'tel', autocomplete: 'tel', maxlength: 50},
        {key: 'methodInstagram', label: 'Instagram', field: 'instagram', detail: 'Your Instagram username', type: 'text', autocomplete: 'off', maxlength: 50}
      ],
      state: 'form', errors: {}, error: ''
    };
  },
  computed: {
    firstName () { return usableHostFirstName(this.host); },
    hasMethod () { return this.methods.some(method => this.form[method.key]); },
    selectedMethods () { return this.methods.filter(method => this.form[method.key]).map(method => method.label).join(' or '); }
  },
  methods: {
    close () { if (this.state !== 'pending') this.$emit('close'); },
    errorText (error) { return Array.isArray(error) ? error.join(' ') : error; },
    send () {
      if (this.state === 'pending' || !this.hasMethod) return;
      this.state = 'pending';
      this.errors = {};
      this.error = '';
      this.$emit('send', {...this.form});
    }
  },
  watch: {
    connectResult (result) {
      if (!result) return;
      if (result.ok === true) {
        this.state = 'success';
        this.$nextTick(() => this.$refs.success.focus());
      } else if (result.state === 'pending') {
        this.state = 'pending';
      } else {
        this.state = 'form';
        this.errors = result.errors || {};
        this.error = result.error || 'We couldn’t send your message. Please check your details and try again.';
      }
    }
  }
}
</script>
<style scoped>
.host-contact { color: #26362e; }
h2 { font-family: Georgia, serif; font-size: 24px; font-weight: 400; margin: 0 0 22px; overflow-wrap: anywhere; }
fieldset { border: 0; padding: 0; margin: 0; min-width: 0; }
label { display: block; margin: 16px 0 6px; font-size: 13px; }
input:not([type=checkbox]), textarea { box-sizing: border-box; display: block; width: 100%; border: 1px solid #8a9188; border-radius: 8px; padding: 10px; background: #fff; color: #26362e; font: inherit; font-size: 14px; }
textarea { resize: vertical; }
.methods { margin: 22px 0 16px; }
legend { float: none; font-size: 14px; line-height: 1.5; margin: 0; }
.hint { color: #62695f; font-size: 12px; margin: 4px 0; }
.contact-method { display: inline-flex; align-items: center; gap: 7px; margin: 6px 16px 6px 0; min-height: 32px; cursor: pointer; }
input[type=checkbox] { width: 18px; height: 18px; accent-color: #26362e; }
.send-button { width: 100%; margin-top: 22px; padding: 13px 16px; border: 0; border-radius: 9px; background: #fff; color: #26362e; box-shadow: inset 0 0 0 1px #d7dcd5; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
.send-button:hover:not(:disabled) { background: #f4f5f1; }
.send-button:disabled { background: #dce0da; color: #50594f; cursor: not-allowed; }
form[aria-busy=true] .send-button { background: #526457; color: #fff; cursor: wait; }
.back-link { display: block; margin: 16px auto 0; padding: 8px 12px; background: transparent; border: 0; color: #26362e; text-decoration: underline; font: inherit; font-size: 13px; cursor: pointer; }
.back-link:disabled { color: #62695f; cursor: wait; }
input:focus-visible, textarea:focus-visible, button:focus-visible { outline: 3px solid #708a64; outline-offset: 3px; }
.field-error { color: #9b2929; font-size: 13px; margin-top: 8px; }
.success { overflow-wrap: anywhere; }
</style>
